import React, { useState } from 'react';
import {
  ArrowLeft,
  Heart,
  Bookmark,
  MessageSquare,
  Share2,
  Users,
  Play,
  BadgeCheck,
  Send,
  ChevronRight,
  ExternalLink,
  X,
} from 'lucide-react';
import { useRobloky } from '../../context/RoblokyContext';
import { GameCard } from './GameCard';
import { GameImg } from './GameImg';
import { PlayModal } from './PlayModal';
import { formatCompact, timeAgo, displayLikes, basePrefix } from '../../utils/format';

// Native Roblox player deep link. When the Roblox app is installed on the
// phone, the OS intercepts this scheme and opens the game directly in the
// app â€” no browser, no iframe.
const ROBLOX_DEEP_LINK = (placeId: string) => `roblox://experiences/start?placeId=${placeId}`;
const ROBLOX_PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.roblox.client';
const ROBLOX_APP_STORE = 'https://apps.apple.com/us/app/roblox/id431946152';

function isMobileDevice(): boolean {
  try {
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return true;
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
  } catch { /* ignore */ }
  return false;
}

function isAndroid(): boolean {
  try {
    return /Android/i.test(navigator.userAgent);
  } catch {
    return false;
  }
}

export const GameDetail: React.FC<{ placeId: string }> = ({ placeId }) => {
  const {
    currentGame: game,
    gameLoading,
    goHome,
    openGame,
    likeGame,
    saveGame,
    commentOnGame,
    openAuthModal,
  } = useRobloky();
  const [imgIdx, setImgIdx] = useState(0);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [showPlay, setShowPlay] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [showShare, setShowShare] = useState(false);

  if (gameLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4 animate-pulse">
        <div className="h-6 w-40 rounded bg-slate-800/60" />
        <div className="aspect-video rounded-2xl bg-slate-800/60" />
        <div className="h-6 w-2/3 rounded bg-slate-800/60" />
        <div className="h-24 rounded bg-slate-800/60" />
      </div>
    );
  }

  if (!game || game.placeId !== placeId) {
    return (
      <div className="text-center py-16 space-y-3 max-w-md mx-auto">
        <p className="text-slate-200 font-bold">Game not found</p>
        <p className="text-xs font-mono text-slate-500">
          It may have been removed. Back to the directory:
        </p>
        <button
          onClick={goHome}
          className="mt-2 px-5 py-2.5 rounded-xl bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-bold hover:bg-red-500/25 transition-all"
        >
          All games
        </button>
      </div>
    );
  }

  const gallery = game.gallery.length > 0 ? game.gallery : game.thumb ? [game.thumb] : [];
  const hero = gallery[Math.min(imgIdx, gallery.length - 1)];

  const submitComment = async () => {
    const text = comment.trim();
    if (!text || posting) return;
    setPosting(true);
    const ok = await commentOnGame(game.placeId, text);
    if (ok) setComment('');
    setPosting(false);
  };

  // Play: on phones, fire the native roblox:// deep link so the installed
  // Roblox app opens the game automatically. If the app is NOT installed the
  // page stays visible â€” after a short delay we show the install prompt with
  // store links instead. On desktop (no app to open) keep the in-app iframe.
  const handlePlay = () => {
    if (!isMobileDevice()) {
      setShowPlay(true);
      return;
    }
    let opened = false;
    const onHide = () => {
      opened = true;
    };
    document.addEventListener('visibilitychange', onHide, { once: true });
    window.addEventListener('pagehide', onHide, { once: true });
    try {
      window.location.href = ROBLOX_DEEP_LINK(game.placeId);
    } catch {
      document.removeEventListener('visibilitychange', onHide);
      setShowInstall(true);
      return;
    }
    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onHide);
      if (!opened && !document.hidden) setShowInstall(true);
    }, 1800);
  };

  const shareUrl = (typeof window !== 'undefined' ? window.location.origin : '') + `${basePrefix()}/game/${game.placeId}/${game.slug}`;
  const shareTitle = `${game.title} - Robloky`;
  const shareText = `Check out ${game.title} on Robloky`;

  const handleShare = async () => {
    if (typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function') {
      try {
        await (navigator as any).share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch { /* fall through */ }
    }
    setShowShare((v) => !v);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch { /* ignore */ }
    setShowShare(false);
  };

  const paragraphs = game.about
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, 6);

  return (
    <article className="max-w-4xl mx-auto space-y-5">
      <nav className="flex items-center gap-1.5 text-xs font-mono text-slate-500">
        <button onClick={goHome} className="flex items-center gap-1 hover:text-slate-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Games
        </button>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-300 truncate">{game.title}</span>
      </nav>

      <header className="space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="rounded-md bg-red-500/15 px-2 py-0.5 text-[10px] font-mono font-bold text-red-300 border border-red-500/30">
            {game.genre}
          </span>
          {game.likePct > 0 && (
            <span className="rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono font-bold text-emerald-300 border border-emerald-500/30">
              {game.likePct}% liked
            </span>
          )}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">{game.title}</h1>
        <p className="flex items-center gap-1.5 text-xs font-mono text-slate-400">
          by <span className="text-slate-200 font-bold">{game.developerFull}</span>
          <BadgeCheck className="h-3.5 w-3.5 text-red-400" />
          <span className="text-slate-600">â€¢</span>
          <span>introduced by {game.authorName}</span>
        </p>
      </header>

      {hero && (
        <figure className="space-y-2">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/40">
            <GameImg src={hero} altSrc={game.galleryAlt[Math.min(imgIdx, game.galleryAlt.length - 1)] ?? game.thumbAlt} alt={`${game.title} screenshot`} eager className="w-full aspect-video object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {gallery.slice(0, 8).map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`shrink-0 w-24 aspect-video overflow-hidden rounded-lg border transition-all ${
                    i === imgIdx ? 'border-red-500 ring-2 ring-red-500/40' : 'border-white/10 opacity-70 hover:opacity-100'
                  }`}
                >
                  <GameImg src={src} altSrc={game.galleryAlt[i] ?? game.thumbAlt} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {game.mediaCaption && (
            <figcaption className="text-[11px] font-mono text-slate-500">{game.mediaCaption}</figcaption>
          )}
        </figure>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-white/10 bg-[#0d111a] p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-emerald-300 font-bold">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            {game.ccu > 0 ? formatCompact(game.ccu) : 'â€”'}
          </div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">playing now</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0d111a] p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-white font-bold">
            <Users className="h-3.5 w-3.5 text-slate-400" />
            {game.visits > 0 ? formatCompact(game.visits) : 'â€”'}
          </div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">total visits</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0d111a] p-3 text-center">
          <div className="flex items-center justify-center gap-1.5 text-white font-bold">
            <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
            {formatCompact(game.comments)}
          </div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">reviews</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={handlePlay}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all shadow-[0_0_20px_rgba(226,35,26,0.35)]"
        >
          <Play className="h-4 w-4 fill-white" />
          Play
        </button>
        <button
          onClick={() => likeGame(game.placeId)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all ${
            game.liked
              ? 'bg-red-500/15 border-red-500/50 text-red-300'
              : 'bg-slate-900 border-white/10 text-slate-300 hover:border-red-500/40'
          }`}
        >
          <Heart className={`h-4 w-4 ${game.liked ? 'fill-red-500 text-red-500' : ''}`} />
          {displayLikes(game.upVotes, game.likes, game.liked)}
        </button>
        <button
          onClick={() => saveGame(game.placeId)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all ${
            game.saved
              ? 'bg-red-500/15 border-red-500/50 text-red-300'
              : 'bg-slate-900 border-white/10 text-slate-300 hover:border-red-500/40'
          }`}
        >
          <Bookmark className={`h-4 w-4 ${game.saved ? 'fill-red-500/40' : ''}`} />
          {game.saved ? 'Saved' : 'Save'}
        </button>
        <div className="relative">
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all bg-slate-900 border-white/10 text-slate-300 hover:border-red-500/40"
          >
            <Share2 className="h-4 w-4" />
            Share
          </button>
          {showShare && (
            <div className="absolute z-20 mt-2 right-0 w-52 rounded-xl border border-white/10 bg-[#0d111a] p-2 shadow-xl space-y-1">
              <button onClick={copyShareLink} className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/5">Copy link</button>
              <a href={"https://twitter.com/intent/tweet?text=" + encodeURIComponent(shareText) + "&url=" + encodeURIComponent(shareUrl)} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/5">X / Twitter</a>
              <a href={"https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(shareUrl)} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/5">Facebook</a>
              <a href={"https://wa.me/?text=" + encodeURIComponent(shareText + ' ' + shareUrl)} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/5">WhatsApp</a>
              <a href={"https://t.me/share/url?url=" + encodeURIComponent(shareUrl) + "&text=" + encodeURIComponent(shareText)} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-white/5">Telegram</a>
            </div>
          )}
        </div>
      </div>

      <section className="rounded-2xl border border-white/10 bg-[#0d111a] p-4 sm:p-5 space-y-3">
        <h2 className="text-sm font-bold text-white font-mono uppercase tracking-wider">
          About {game.title}
        </h2>
        <div className="space-y-2.5 text-sm leading-relaxed text-slate-300">
          {paragraphs.length > 0 ? (
            paragraphs.map((p, i) => <p key={i}>{p}</p>)
          ) : (
            <p>
              {game.title} is a {game.genre} experience on Roblox by {game.developerFull}.
            </p>
          )}
        </div>
        {game.features.length > 0 && (
          <div className="pt-1">
            <h3 className="text-xs font-bold text-white font-mono uppercase tracking-wider mb-2">
              Key features
            </h3>
            <ul className="space-y-1.5">
              {game.features.map((f, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-300">
                  <span className="text-red-400 font-bold">â€¢</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 text-xs">
          {[
            ['Developer', game.developerFull],
            ['Publisher', game.publisher],
            ['Genre', game.genres.join(', ') || game.genre],
            ['Platforms', game.platforms.join(', ') || 'Roblox (PC, mobile, console)'],
            ['Max players', game.maxPlayers > 0 ? String(game.maxPlayers) : 'â€”'],
            ['Price', 'Free-to-play'],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg bg-black/30 border border-white/5 p-2.5">
              <dt className="font-mono text-slate-500 text-[10px] uppercase">{k}</dt>
              <dd className="text-slate-200 font-semibold mt-0.5 line-clamp-2">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section id="comments" className="rounded-2xl border border-white/10 bg-[#0d111a] p-4 sm:p-5 space-y-4 scroll-mt-20">
        <h2 className="flex items-center gap-2 text-sm font-bold text-white font-mono uppercase tracking-wider">
          <MessageSquare className="h-4 w-4 text-red-400" />
          Player reviews ({game.comments})
        </h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitComment();
            }}
            maxLength={2000}
            placeholder="Share your review of this game..."
            className="flex-1 px-3.5 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
          />
          <button
            onClick={submitComment}
            disabled={posting || !comment.trim()}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Post</span>
          </button>
        </div>
        {game.commentsList.length === 0 ? (
          <p className="text-xs font-mono text-slate-500">
            No reviews yet â€” be the first to review {game.title}.
          </p>
        ) : (
          <ul className="space-y-3">
            {game.commentsList.map((c) => (
              <li key={c.id} className="flex gap-2.5 rounded-xl bg-black/30 border border-white/5 p-3">
                <img
                  src={c.author.avatar}
                  alt=""
                  loading="lazy"
                  className="h-8 w-8 rounded-full object-cover border border-white/10 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-200">{c.author.name}</span>
                    <span className="text-[10px] font-mono text-red-400/80">{c.author.tag}</span>
                    <span className="text-[10px] font-mono text-slate-600">{timeAgo(c.createdAt)}</span>
                  </div>
                  <p className="text-sm text-slate-300 mt-1 leading-relaxed break-words">{c.content}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {game.related.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-bold text-white font-mono uppercase tracking-wider">
            More Roblox games
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {game.related.map((r) => (
              <GameCard key={r.placeId} game={r} />
            ))}
          </div>
        </section>
      )}
      {showPlay && (
        <PlayModal
          placeId={game.placeId}
          title={game.title}
          robloxUrl={game.robloxUrl}
          onClose={() => setShowPlay(false)}
        />
      )}
      {showInstall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0d111a] p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-bold text-white">Roblox player not found</h3>
              <button
                onClick={() => setShowInstall(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm leading-relaxed text-slate-300">
              To play <span className="font-bold text-white">{game.title}</span> you need the
              free Roblox app installed on your phone. Install it, then press Play again.
            </p>
            <div className="space-y-2">
              <a
                href={isAndroid() ? ROBLOX_PLAY_STORE : ROBLOX_APP_STORE}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all"
              >
                <ExternalLink className="h-4 w-4" />
                Install Roblox ({isAndroid() ? 'Google Play' : 'App Store'})
              </a>
              <a
                href={isAndroid() ? ROBLOX_APP_STORE : ROBLOX_PLAY_STORE}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl border border-white/10 bg-slate-900 text-slate-200 text-sm font-bold hover:border-red-500/40 transition-all"
              >
                {isAndroid() ? 'App Store' : 'Google Play'}
              </a>
              <a
                href={game.robloxUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-xs font-mono text-slate-500 hover:text-slate-300 transition-all"
              >
                Continue in browser instead
              </a>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};
